import { EventType, InteractionRequiredAuthError, LogLevel, PublicClientApplication, } from "@azure/msal-browser";
const isMsalErrorCode = (error, errorCode) => error !== null &&
    typeof error === "object" &&
    "errorCode" in error &&
    error.errorCode === errorCode;
export class Authentication {
    msalInstance;
    userPromise = undefined;
    user = undefined;
    interactiveAuthPromise = undefined;
    loggedInPromise;
    redirectHandledPromise;
    redirectHandledPromiseRaw;
    constructor(init) {
        this.msalInstance = new PublicClientApplication({
            auth: {
                clientId: init.clientId,
                authority: `https://login.microsoftonline.com/${init.tenantId}`,
                navigateToLoginRequestUrl: true,
            },
            cache: {
                cacheLocation: "sessionStorage",
                storeAuthStateInCookie: false,
            },
            system: {
                loggerOptions: {
                    loggerCallback: (level, message, containsPii) => {
                        const shouldLog = new URLSearchParams(window.location.search).get("log") === "true";
                        if (!shouldLog || containsPii) {
                            return;
                        }
                        switch (level) {
                            case LogLevel.Error:
                                return console.error(message);
                            case LogLevel.Info:
                                return console.info(message);
                            case LogLevel.Verbose:
                                return console.debug(message);
                            case LogLevel.Warning:
                                return console.warn(message);
                        }
                    },
                },
            },
        });
        const shouldLog = new URLSearchParams(window.location.search).get("log") === "true";
        this.redirectHandledPromiseRaw = this.msalInstance.handleRedirectPromise();
        this.redirectHandledPromiseRaw.catch((error) => {
            if (shouldLog) {
                console.error("handleRedirectPromise failed: %o", error);
            }
        });
        this.redirectHandledPromise = this.redirectHandledPromiseRaw.catch(() => null);
        const initializeActiveAccountFromCache = () => {
            if (this.msalInstance.getActiveAccount() !== null) {
                return true;
            }
            const account = this.msalInstance.getAllAccounts()[0];
            if (account != null) {
                this.msalInstance.setActiveAccount(account);
                return true;
            }
            return false;
        };
        this.loggedInPromise = new Promise((resolve) => {
            if (initializeActiveAccountFromCache()) {
                resolve();
                return;
            }
            this.redirectHandledPromise.then(() => {
                if (initializeActiveAccountFromCache()) {
                    resolve();
                }
            });
            this.msalInstance.addEventCallback((event) => {
                const payload = event.payload;
                if (event.eventType === EventType.LOGIN_SUCCESS &&
                    payload.account !== null) {
                    this.msalInstance.setActiveAccount(payload.account);
                    resolve();
                }
            });
        });
    }
    runInteractiveAuth(start, { resolveInitiator = false } = {}) {
        if (this.interactiveAuthPromise !== undefined) {
            return this.interactiveAuthPromise;
        }
        let started;
        try {
            started = start();
        }
        catch (error) {
            this.interactiveAuthPromise = undefined;
            throw error;
        }
        this.interactiveAuthPromise = (async () => {
            try {
                await started;
                return new Promise(() => undefined);
            }
            catch (error) {
                if (isMsalErrorCode(error, "interaction_in_progress")) {
                    return new Promise(() => undefined);
                }
                this.interactiveAuthPromise = undefined;
                throw error;
            }
        })();
        if (!resolveInitiator) {
            return this.interactiveAuthPromise;
        }
        return started.then(() => undefined, (error) => {
            if (isMsalErrorCode(error, "interaction_in_progress")) {
                return this.interactiveAuthPromise;
            }
            this.interactiveAuthPromise = undefined;
            throw error;
        });
    }
    async login(request) {
        await this.redirectHandledPromise;
        return this.runInteractiveAuth(() => this.msalInstance.loginRedirect(request), { resolveInitiator: true });
    }
    async logout(config) {
        await this.redirectHandledPromise;
        return this.runInteractiveAuth(() => this.msalInstance.logoutRedirect(config), { resolveInitiator: true });
    }
    getToken = async (request) => {
        await this.redirectHandledPromise;
        await this.loggedInPromise;
        const response = await this.msalInstance
            .acquireTokenSilent(request)
            .catch(async (error) => {
            const isInteractionRequired = error instanceof InteractionRequiredAuthError;
            const isSilentIframeTimeout = isMsalErrorCode(error, "monitor_window_timeout");
            if (isInteractionRequired || isSilentIframeTimeout) {
                return this.runInteractiveAuth(() => this.msalInstance.acquireTokenRedirect(request));
            }
            throw error;
        });
        return response.accessToken;
    };
    async getUserImpl() {
        const accessToken = await this.getToken({ scopes: ["User.Read"] });
        const options = {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        };
        const [profile, photo] = (await Promise.allSettled([
            fetch("https://graph.microsoft.com/beta/me?$select=displayName,jobTitle,givenName,surname,mail,userPrincipalName,officeLocation,preferredLanguage,country,department,onPremisesExtensionAttributes", options).then((r) => r.json()),
            fetch("https://graph.microsoft.com/v1.0/me/photo/$value", options)
                .then((r) => r.blob())
                .then((imageBlob) => new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(r.result?.toString());
                r.onerror = reject;
                r.readAsDataURL(imageBlob);
            })),
        ]).then((r) => r.map((x) => (x.status === "fulfilled" ? x.value : undefined))));
        if (profile === undefined) {
            return undefined;
        }
        return {
            profile,
            countryCode: profile.onPremisesDistinguishedName
                ?.split(",")
                .filter((part) => part.trim().startsWith("OU="))[2]
                ?.trim()
                .substring("OU=".length),
            photo,
            unit: profile.onPremisesExtensionAttributes?.extensionAttribute13,
        };
    }
    async getUserGroups(groupPrefix = "") {
        const accessToken = await this.getToken({
            scopes: ["email", "openid", "profile", "User.Read"],
        });
        const options = {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                ConsistencyLevel: "eventual",
            },
        };
        const userPrincipalName = (await this.getUser()).profile
            .userPrincipalName;
        const apiURL = `https://graph.microsoft.com/v1.0/users/${userPrincipalName}/MemberOf/microsoft.graph.group?$select=id&$search="displayName:${groupPrefix}"`;
        const result = (await (await fetch(apiURL, options)).json());
        return result.value.map((group) => group.id);
    }
    getUser = async () => {
        if (this.user !== undefined) {
            return this.user;
        }
        if (this.userPromise !== undefined) {
            return this.userPromise;
        }
        {
            this.userPromise = this.getUserImpl();
            this.user = await this.userPromise;
            this.userPromise = undefined;
        }
        return this.user;
    };
}
//# sourceMappingURL=authentication.js.map